/**
 * Le edizioni del catalogo: portarle dentro, e metterne una in vigore.
 *
 * **Sono due atti, e la pagina non li unisce.** `importRelease` non attiva
 * niente, e non esiste una funzione che faccia le due cose insieme: un tasto
 * «importa e attiva» renderebbe impossibile correggere un refuso
 * dell'estrazione senza cambiare, nello stesso istante, le regole applicate a
 * chi sta lavorando.
 *
 * **I punti aperti si leggono.** «Sette conflitti» non dice a nessuno che cosa
 * chiedere al committente: `readConflicts` porta quale criticita' e quale
 * codice, cosi' l'elenco si usa senza riaprire il documento.
 */
import { get, newKey, send } from '../api'
import type { MessageKey } from '../i18n/messages'
import { tx } from '../i18n/t'

export interface Release {
  id: string
  document: string
  version: string
  title: string | null
  issuer: string | null
  issued_on: string | null
  imported_at: string
  /** Di attiva ce n'e' esattamente una: lo garantisce la base dati. */
  active: boolean
  activated_at: string | null
  activated_by_id: string | null
  criticalities: number
  swim_codes: number
  conflicts: number
}

export interface Imported {
  release_id: string
  release: string
  rule: string
  created: boolean
  /** Sempre falso: importare non mette in vigore. */
  active: boolean
  added: Record<string, number>
  updated: Record<string, number>
  unchanged: Record<string, number>
  conflicts: number
}

export interface Conflict {
  kind: string
  owner_type: string
  owner: string
  detail: unknown
}

export function readReleases(): Promise<Release[]> {
  return get<Release[]>('/catalog/releases')
}

/** Porta dentro l'estrazione. Non la mette in vigore, nemmeno se e' la prima. */
export function importRelease(payload: unknown, key = newKey()): Promise<Imported> {
  return send<Imported>('/catalog/releases/import', payload, key)
}

/** Mette in vigore questa edizione e spegne l'altra, in una sola transazione. */
export function activateRelease(releaseId: string, key = newKey()): Promise<Release> {
  return send<Release>(`/catalog/releases/${releaseId}/activate`, {}, key)
}

export function readConflicts(releaseId: string): Promise<Conflict[]> {
  return get<Conflict[]>(`/catalog/releases/${releaseId}/conflicts`)
}

/** Il nome dei generi di conflitto, in italiano.
 *
 * I codici arrivano dall'estrazione (`importer.CONFLICT_FLAGS`); qui si
 * traducono per chi legge, e cio' che non e' previsto si mostra col proprio
 * codice invece di sparire. */
const CONFLICTS: Record<string, MessageKey> = {
  disputed_levels: 'catalog.conflictDisputed',
  card_missing: 'catalog.conflictMissing',
  levels_unknown: 'catalog.conflictUnknown',
}

export function conflictLabel(kind: string): string {
  const key = CONFLICTS[kind]
  return key !== undefined ? tx(key) : kind
}
